import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  IterableDiffer,
  IterableDiffers,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import * as _ from 'lodash';
import { ReplaySubject } from 'rxjs';
import { I18nTranslator } from '../../i18n/translate';
import { I18nService } from '../../services/i18n.service';
import { MapService } from '../../services/map.service';
import { GOOGLE_MAPS_API_KEY } from '../../utils/env';
import { GoogleMapProvider } from './google-map.provider';
import './leaflet-google';
import { MapProvider } from './map.provider';
import { ScriptLoaderService } from './script-loader.service';
declare let turf: any;
export type ControlPosition = 'topleft' | 'topright' | 'bottomleft' | 'bottomright';

export interface MapOptions {
  scale?: boolean;
  center?: number[];
  zoom?: any;
  draw?: any;
  zoomInTitle?: string;
  zoomOutTitle?: string;
  mapIntersectionText?: string;
}

export interface ZoomOptions {
  position?: ControlPosition;
  defaultValue: number;
  min?: number;
  max?: number;
}
export interface DrawOptions {
  position?: ControlPosition;
  color?: string;
  fillColor?: string;
  errorColor?: string;
  rectangle?: boolean;
  circle?: boolean;
  circlemarker?: boolean;
  marker?: boolean;
  polygon?: boolean;
  opacity?: number;
}

@Component({
  standalone: true,
  selector: 'agr-map',
  templateUrl: './map.component.html',
  providers: [ScriptLoaderService, MapProvider],
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./map.component.scss'],
})
export class MapComponent implements AfterViewInit, OnChanges {
  /* ***
   * Defaults
   */
  public static DEFAULT_ZOOM = 7;
  public static DEFAULT_CONTROLS_POSITION: ControlPosition = 'topright';

  public static POLY_STYLE = {
    color: '#0000ff',
    fillColor: '#0000ff',
  };

  public static DEFAULT_DRAW_OPTIONS: DrawOptions = {
    position: MapComponent.DEFAULT_CONTROLS_POSITION,
    color: MapComponent.POLY_STYLE['color'],
    fillColor: MapComponent.POLY_STYLE['fillColor'],
    errorColor: '#e1e100',
    rectangle: false,
    circle: false,
    marker: false,
    circlemarker: false,
    polygon: true,
    opacity: 0.6,
  };
  public static DEFAULT_POSITION = [-31.95376, 115.86671];
  public static DEFAULT_OPTIONS: MapOptions = {
    scale: true,
    center: MapComponent.DEFAULT_POSITION,
    zoom: {
      position: MapComponent.DEFAULT_CONTROLS_POSITION,
      defaultValue: 7,
      min: 1,
      max: 17,
    },
    draw: false,
  };

  /* ***
   * Public attrs
   */
  @Input()
  public zoom = MapComponent.DEFAULT_ZOOM;
  @Input()
  public options: MapOptions = MapComponent.DEFAULT_OPTIONS;

  public area = 0;

  @Output()
  public draw = new EventEmitter<L.LayerGroup>();

  @Input()
  public shapes: L.FeatureGroup = L.featureGroup();

  @Output()
  public warning = new EventEmitter<any>();

  @Output()
  public map = new EventEmitter<L.Map>();
  /* ***
   * Private attrs
   */
  private _map: L.Map;
  private _ready$ = new ReplaySubject();
  private _differ: IterableDiffer<any>;
  private _provider: MapProvider;

  /* ***
   * Lifecycle
   */
  constructor(
    private _elementRef: ElementRef,
    private mapService: MapService,
    private i18nTranslator: I18nTranslator,
    private i18nService: I18nService,
    differs: IterableDiffers,
  ) {
    this._differ = differs.find([]).create(null);
    this._provider = new GoogleMapProvider(GOOGLE_MAPS_API_KEY, 'HYBRID');
  }

  ngAfterViewInit(): void {
    // fill options with default values
    const center = [this.mapService.DEFAULT_LAT, this.mapService.DEFAULT_LONG];
    this.options = _.defaultsDeep({}, { center }, this.options, MapComponent.DEFAULT_OPTIONS);
    if (!_.isBoolean(this.options.draw)) {
      this.options.draw = _.defaultsDeep(this.options.draw, MapComponent.DEFAULT_DRAW_OPTIONS);
    }
    this.options.zoomInTitle = this.i18nTranslator.translate('map.zoomIn');
    this.options.zoomOutTitle = this.i18nTranslator.translate('map.zoomOut');
    this.options.mapIntersectionText = this.i18nTranslator.translate('map.intersection');

    console.log(this.options);
    // init the map
    const el = this._elementRef.nativeElement.querySelector('.leaflet-map');
    this._map = L.map(el, _makeLeafletOptions(this.options));
    this.map.emit(this._map);
    //let osm = L.tileLayer('http://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 18, attribution: "Hello" })

    const bbox = this.i18nService.i18nInfo.country.bbox.coordinates;
    this._ready$.subscribe(() => {
      this._provider.addToMap(this._map);
      //this._map.addLayer(osm);

      // apply options
      _applyMapOptions(this.options, this._map);

      this._map.fitBounds([
        [bbox[0][1], bbox[0][0]],
        [bbox[1][1], bbox[1][0]],
      ]);

      // enable draw ?
      if (this.options.draw) {
        this.setupLeafletDraw();
      }
    });
    this._ready$.next(null);
  }

  ngOnChanges(changes: SimpleChanges | any): void {
    this._ready$.subscribe(() => {
      if (!_.isUndefined(changes.zoom)) {
        if (changes.zoom.currentValue !== changes.zoom.previousValue) {
          this.zoom = changes.zoom.currentValue || MapComponent.DEFAULT_ZOOM;
          this._map.setZoom(this.zoom);
        }
      }

      if (!_.isUndefined(changes.shapes)) {
        if (changes.shapes.currentValue !== changes.shapes.previousValue) {
          this._map.addLayer(changes.shapes.currentValue);
          changes.shapes.currentValue.on('layeradd', (event: any, layername: any) => {
            this.onDraw(event.target);
          });

          if (changes.shapes.previousValue) {
            this._map.removeLayer(changes.shapes.previousValue);
          }
          this.onDraw(this.shapes);
        }
      }
    });
  }

  /* ***
   * Private
   */

  private setupLeafletDraw(): void {
    // init leaflet draw
    const drawControl = new (L.Control as any).Draw(_makeDrawOptions(this.options, this.shapes));
    (L as any).drawLocal.draw.toolbar.buttons.polygon = this.i18nTranslator.translate('map.dessin');
    (L as any).drawLocal.draw.toolbar.actions.title = this.i18nTranslator.translate('map.cancel');
    (L as any).drawLocal.draw.toolbar.actions.text = this.i18nTranslator.translate('map.cancel.short');
    (L as any).drawLocal.draw.toolbar.finish.title = this.i18nTranslator.translate('map.terminer');
    (L as any).drawLocal.draw.toolbar.finish.text = this.i18nTranslator.translate('map.terminer');
    (L as any).drawLocal.draw.toolbar.undo.title = this.i18nTranslator.translate('map.supprDernierPoint');
    (L as any).drawLocal.draw.toolbar.undo.text = this.i18nTranslator.translate('map.supprDernierPoint');

    (L as any).drawLocal.draw.handlers.polygon.tooltip.start = this.i18nTranslator.translate('map.clicCommencer');
    (L as any).drawLocal.draw.handlers.polygon.tooltip.cont = this.i18nTranslator.translate('map.clicPoursuivre');
    (L as any).drawLocal.draw.handlers.polygon.tooltip.end = this.i18nTranslator.translate('map.clicPremierPoint');

    (L as any).drawLocal.edit.toolbar.actions.save.title = this.i18nTranslator.translate('map.sauver');
    (L as any).drawLocal.edit.toolbar.actions.save.text = this.i18nTranslator.translate('map.sauver');
    (L as any).drawLocal.edit.toolbar.actions.cancel.title = this.i18nTranslator.translate('map.cancel.short');
    (L as any).drawLocal.edit.toolbar.actions.cancel.text = this.i18nTranslator.translate('map.cancel.short');
    (L as any).drawLocal.edit.toolbar.actions.clearAll.title = this.i18nTranslator.translate('map.suppr');
    (L as any).drawLocal.edit.toolbar.actions.clearAll.text = this.i18nTranslator.translate('map.suppr');

    (L as any).drawLocal.edit.toolbar.buttons.edit = this.i18nTranslator.translate('map.modifier');
    (L as any).drawLocal.edit.toolbar.buttons.editDisabled = this.i18nTranslator.translate('map.noParcelle');
    (L as any).drawLocal.edit.handlers.edit.tooltip.text = this.i18nTranslator.translate('map.clicPointsMove');
    (L as any).drawLocal.edit.handlers.edit.tooltip.subtext = this.i18nTranslator.translate('map.clicCancel');
    (L as any).drawLocal.edit.toolbar.buttons.remove = this.i18nTranslator.translate('map.supprParcelle');
    (L as any).drawLocal.edit.toolbar.buttons.removeDisabled = this.i18nTranslator.translate('map.noParcelleSuppr');
    (L as any).drawLocal.edit.handlers.remove.tooltip.text = this.i18nTranslator.translate('map.clicParcelleSuppr');

    this._map.addLayer(this.shapes);
    this._map.addControl(drawControl);

    this._map.on('draw:created', (e: any) => {
      const poly = <L.Polygon>e.layer;
      // no polygons yet
      if (!this.shapes.getLayers().length) {
        // draw the first poly
        this.shapes.addLayer(poly);
      } else {
        const existing = <L.Polygon>this.shapes.getLayers()[0];
        if (_isPolygonInsidePolygon(poly, existing)) {
          // draw a hole inside the first polygon
          _cutPolygon(this.shapes, existing, poly);
        } else {
          this.shapes.clearLayers();
          this.shapes.addLayer(poly);
        }
      }

      this.onDraw(this.shapes);
    });

    this._map.on('draw:edited', (e: any) => {
      // no
      const layers = e.layers;
      this.onDraw(layers);
    });

    this._map.on('draw:editstop', (e: any) => {
      this.warning.emit(null);
    });

    this._map.on('draw:editvertex', (e: any) => {
      const poly = e.poly;
      this.area = computeArea(poly);
      this.draw.emit(e.layers);
    });
  }

  private onDraw(layerGroup: L.LayerGroup): void {
    if (layerGroup.getLayers().length > 0) {
      const drawOptions = _makeDrawOptions(this.options, this.shapes);
      (<L.Polygon>layerGroup.getLayers()[0]).setStyle(drawOptions.draw.polygon.shapeOptions);

      const p = <L.Polygon>layerGroup.getLayers()[0];
      this.area = computeArea(p);
    }

    this.draw.emit(layerGroup);
  }
}

function _makeLeafletOptions(options: MapOptions): L.MapOptions {
  const z = _.isObject(options.zoom)
    ? (options.zoom as ZoomOptions)
    : (MapComponent.DEFAULT_OPTIONS.zoom as ZoomOptions);

  return {
    zoomControl: false,
    attributionControl: false,
    center: L.latLng(options.center[0], options.center[1]),
    zoom: _.isNumber(options.zoom) ? (options.zoom as number) : z.defaultValue,
    minZoom: z.min,
    maxZoom: z.max,
    zoomAnimation: false,
  };
}

function computeArea(poly: L.Polygon): number {
  let area = 0.0;
  const latlngs = <any>poly.getLatLngs();
  const outer: L.LatLng[] = latlngs[0];
  const inner: L.LatLng[][] = latlngs.slice(1);

  area += L.GeometryUtil.geodesicArea(outer);

  inner.forEach((h: any) => {
    area -= (L as any).GeometryUtil.geodesicArea(h);
  });

  return area;
}

function _makeDrawOptions(options: MapOptions, drawnItemsFeatureGroup: L.FeatureGroup): any {
  let o: any;
  if (_.isObject(options.draw)) {
    o = options.draw as DrawOptions;
  } else if (!options.draw) {
    o = MapComponent.DEFAULT_DRAW_OPTIONS;
  } else {
    throw new Error('invalid state');
  }

  const shapeOptions = {
    allowIntersection: false, // Restricts shapes to simple polygons
    drawError: {
      color: o.errorColor, // Color the shape will turn when intersects
      message: options.mapIntersectionText, // Message that will show when intersect
    },
    shapeOptions: {
      color: o.color,
      fillColor: o.fillColor,
    },
  };

  return {
    position: o.position,
    draw: {
      polyline: o.polyline ? shapeOptions : false,
      rectangle: o.rectangle ? shapeOptions : false,
      circle: false,
      marker: false,
      polygon: o.polygon ? shapeOptions : false,
      circlemarker: o.circlemarker ? shapeOptions : false,
    },
    edit: {
      featureGroup: drawnItemsFeatureGroup,
      remove: true,
      edit: {
        selectedPathOptions: {
          maintainColor: true,
          opacity: o.opacity,
        },
      },
    },
  };
}

function _hasZoom(options: MapOptions) {
  return _.isObject(options.zoom);
}

function _applyMapOptions(options: MapOptions, map: L.Map) {
  if (_hasZoom(options)) {
    if (_.isObject(options.zoom)) {
      L.control
        .zoom({
          position: (options.zoom as any).position,
          zoomInTitle: options.zoomInTitle,
          zoomOutTitle: options.zoomOutTitle,
        })
        .addTo(map);
    } else {
      L.control
        .zoom({
          position: MapComponent.DEFAULT_CONTROLS_POSITION,
          zoomInTitle: options.zoomInTitle,
          zoomOutTitle: options.zoomOutTitle,
        })
        .addTo(map);
    }
  }
  if (options.scale) {
    L.control.scale().addTo(map);
  }
}

function _isPointInsidePolygon(latlng: any, poly: any) {
  const polyPoints = (<any>poly.getLatLngs())[0];
  const x = latlng.lat,
    y = latlng.lng;

  let inside = false;
  for (let i = 0, j = polyPoints.length - 1; i < polyPoints.length; j = i++) {
    const xi = polyPoints[i].lat,
      yi = polyPoints[i].lng;
    const xj = polyPoints[j].lat,
      yj = polyPoints[j].lng;

    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) {
      inside = !inside;
    }
  }

  return inside;
}

function _cutPolygon(shapes: L.FeatureGroup, poly: any, hole: any): void {
  const hasHole = poly.getLatLngs().length > 1;
  let holes: any = null;
  if (hasHole) {
    holes = poly
      .getLatLngs()
      .slice(1)
      .map((h: any) => new L.Polygon(h).toGeoJSON() as any);
    hole = hole.toGeoJSON() as any;
    for (const h of holes) {
      hole = turf.union(hole, h);
    }

    if (hole.geometry.type === 'MultiPolygon') {
      const polyHole: L.Polygon = new L.GeoJSON(hole).getLayers()[0] as any;
      holes = [];
      for (const h of polyHole.getLatLngs()) {
        holes.push((<any>h)[0] as any);
      }
    } else {
      holes = (new L.GeoJSON(hole).getLayers()[0] as any).getLatLngs();
    }
  } else {
    // array of arrays
    holes = hole.getLatLngs();
  }

  const contours = [poly.getLatLngs()[0]];
  for (const h of holes) {
    contours.push(h);
  }
  poly = new L.Polygon(contours);
  shapes.clearLayers();
  shapes.addLayer(poly);
  shapes.setStyle({ color: '#0000ff', fillColor: '#0000ff' });
}

function _isPolygonInsidePolygon(poly1: L.Polygon, poly2: L.Polygon): boolean {
  for (const p of (<any>poly1.getLatLngs())[0]) {
    if (!_isPointInsidePolygon(p, poly2)) {
      return false;
    }
  }

  return true;
}

function CheckSizePoly(poly: L.Polygon) {
  const area = computeArea(poly);
  if (area < 10000) {
    return true;
  } else {
    return false;
  }
}
